import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';

@Injectable({
    providedIn: 'root'
})
export class EncryptionDecryptionService {

    private readonly CHARSET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_';
    private readonly MAX_LENGTH = 8;

    // Simple in-memory cache to enhance decryption performance
    private encryptionCache: Map<string, string> = new Map();
    private decryptionCache: Map<string, string[]> = new Map();

    key = CryptoJS.enc.Utf8.parse('msoe837%)ks&!6eb');
    iv = CryptoJS.enc.Utf8.parse('p10Cu&@m3idh9so5');

    // Encrypt a single value
    setEncryption(value: any): string {
        const jsonText = JSON.stringify(value);
        const encrypted = CryptoJS.AES.encrypt(
            jsonText,
            this.key,
            {
                keySize: 128 / 8,
                iv: this.iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
            }
        );
        return encrypted.toString();
    }

    // Decrypt a single value
    setDecryption(encryptedText: string): any {
        const decrypted = CryptoJS.AES.decrypt(encryptedText, this.key, {
            keySize: 128 / 8,
            iv: this.iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });
        const decryptedJsonText = decrypted.toString(CryptoJS.enc.Utf8);
        return JSON.parse(decryptedJsonText);
    }

    //   // Encrypt multiple device serials into one string
    //   encryptDeviceSerials(deviceSerials: string[]): string {
    //     // Create an object with the array of serials
    //     const serialsObject = { serials: deviceSerials };

    //     // Use the existing encryption method
    //     return this.setEncryption(serialsObject);
    //   }

    //   // Decrypt and extract device serials from encrypted string
    //   decryptDeviceSerials(encryptedText: string): string[] {
    //     // Decrypt using the existing method
    //     const decryptedObject = this.setDecryption(encryptedText);

    //     // Return the array of serials
    //     return decryptedObject.serials || [];
    //   }

    /**
 * Encrypt multiple device serials into one string (max 8 chars)
 */
    encryptDeviceSerials(deviceSerials: string[]): string {
        if (!deviceSerials || deviceSerials.length === 0) {
            return '00000000';
        }

        // Use your existing encryption method but encode the result compactly
        const encrypted = this.setEncryption(deviceSerials);

        // Create a short hash of the encrypted string
        const shortHash = this.createShortHash(encrypted);

        // Store the mapping for later retrieval
        const mappingKey = `deviceSerial_${shortHash}`;
        localStorage.setItem(mappingKey, encrypted);

        return shortHash;
    }

    /**
     * Decrypt and extract device serials from encrypted string
     */
    decryptDeviceSerials(encryptedText: string): string[] {
        if (!encryptedText || encryptedText.length !== this.MAX_LENGTH) {
            return [];
        }

        try {
            // Retrieve the full encrypted string using the short hash
            const mappingKey = `deviceSerial_${encryptedText}`;
            const fullEncryptedString = localStorage.getItem(mappingKey);

            if (!fullEncryptedString) {
                return [];
            }

            // Decrypt using your existing method
            return this.setDecryption(fullEncryptedString);

        } catch (error) {
            console.error('Decryption error:', error);
            return [];
        }
    }

    /**
     * Create a short hash from a longer string
     */
    private createShortHash(input: string): string {
        let hash = 0;
        for (let i = 0; i < input.length; i++) {
            const char = input.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }

        // Convert to positive number and encode
        const positiveHash = Math.abs(hash);
        let encoded = this.encodeToBase64(positiveHash);

        // Ensure exactly 8 characters
        return this.padOrTruncate(encoded, this.MAX_LENGTH);
    }

    /**
     * Generate a hash from a collection of device serials
     * This is a critical function that needs to be customized for your serial format
     */
    private hashSerials(serials: string[]): number {
        let hash = 0;

        // Process each serial to extract unique numeric identifiers
        serials.forEach((serial, index) => {
            // Example technique: extract numeric parts and combine them
            const numericPart = this.getNumericValue(serial);

            // Combine in a way that preserves uniqueness
            hash += numericPart * Math.pow(100, index);
        });

        return hash % 281474976710656; // Max value for 8 chars in base64 encoding
    }

    /**
     * Extract a numeric value from a device serial
     * Customize based on your serial format
     */
    private getNumericValue(serial: string): number {
        // Example: extract numeric characters and convert to number
        // You should adapt this based on your specific serial format
        const numericChars = serial.replace(/[^0-9]/g, '');

        // Use last 6 digits to keep the value manageable
        const significantDigits = numericChars.slice(-6);

        return parseInt(significantDigits || '0', 10);
    }

    /**
     * Encode a number into a compact base64-like representation
     */
    private encodeToBase64(value: number): string {
        if (value === 0) return this.CHARSET[0];

        let result = '';
        let remaining = value;

        while (remaining > 0) {
            result = this.CHARSET[remaining % 64] + result;
            remaining = Math.floor(remaining / 64);
        }

        return result;
    }

    /**
     * Decode a base64-like string back to a number
     */
    private decodeFromBase64(encoded: string): number {
        let value = 0;

        for (let i = 0; i < encoded.length; i++) {
            const char = encoded[i];
            const charValue = this.CHARSET.indexOf(char);

            if (charValue === -1) {
                throw new Error(`Invalid character in encoded string: ${char}`);
            }

            value = value * 64 + charValue;
        }

        return value;
    }

    /**
     * Add a simple checksum character
     */
    private addChecksum(encoded: string): string {
        let sum = 0;

        // Calculate sum of character values
        for (let i = 0; i < encoded.length; i++) {
            sum += this.CHARSET.indexOf(encoded[i]);
        }

        // Get checksum character
        const checksum = this.CHARSET[sum % 64];

        return encoded + checksum;
    }

    /**
     * Verify the checksum of an encoded string
     */
    private verifyChecksum(encoded: string): boolean {
        if (encoded.length <= 1) return false;

        const datapart = encoded.substring(0, encoded.length - 1);
        const checksum = encoded[encoded.length - 1];

        let sum = 0;
        for (let i = 0; i < datapart.length; i++) {
            sum += this.CHARSET.indexOf(datapart[i]);
        }

        return checksum === this.CHARSET[sum % 64];
    }

    /**
     * Ensure string is exactly the specified length
     */
    private padOrTruncate(str: string, length: number): string {
        if (str.length > length) {
            return str.substring(0, length);
        }

        return str.padStart(length, '0');
    }

    /**
     * Reconstruct device serials from the numeric hash
     * This function must be customized based on your serial format
     */
    private reconstructSerials(value: number): string[] {
        // This is a placeholder implementation
        const serials: string[] = [];
        let remaining = value;

        // Extract up to 4 device serials from the hash
        for (let i = 0; i < 4; i++) {
            const divider = Math.pow(100, i);
            const serialPart = Math.floor(remaining / divider) % 100;

            if (serialPart > 0) {
                // This creates generic serials, not your actual ones
                serials.push(`DEVICE-${serialPart.toString().padStart(6, '0')}`);
            }
            remaining -= serialPart * divider;
        }
        return serials;
    }
}